import { io } from 'socket.io-client';
import { API_URL } from '../config/api';

/**
 * 🔌 SocketService - Cliente Socket.IO para Notificações em Tempo Real (SINGLETON)
 * 
 * Responsabilidades:
 * - Conectar com servidor Socket.IO
 * - Autenticação via JWT
 * - Receber notificações em tempo real
 * - Gerenciar reconexão automática
 * - Garantir apenas UMA instância ativa (singleton pattern)
 */

class SocketService {
  constructor() {
    // Singleton GLOBAL: resistente ao Hot Module Replacement do React
    if (window.__SOCKET_SERVICE_INSTANCE__) {
      console.log(`♻️ Reutilizando instância GLOBAL do SocketService: ${window.__SOCKET_SERVICE_INSTANCE__.instanceId}`);
      return window.__SOCKET_SERVICE_INSTANCE__;
    }
    
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 5000; // 5 segundos
    this.instanceId = Math.random().toString(36).substring(7); // ID único da instância
    
    // Log apenas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log(`🆔 SocketService instance created: ${this.instanceId}`);
    }
    
    // Armazenar instância no objeto GLOBAL do navegador
    window.__SOCKET_SERVICE_INSTANCE__ = this;
  }

  /**
   * 🔗 Conectar ao servidor Socket.IO
   */
  connect(token) {
    try {
      // ⚠️ IMPORTANTE: Se já tem socket (conectado OU desconectado), NUNCA criar outro!
      // Isso evita múltiplas conexões que causam "transport close"
      if (this.socket) {
        if (!this.socket.connected) {
          this.socket.connect();
        }
        return;
      }

      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔌 [${this.instanceId}] Criando nova conexão Socket.IO...`, {
          API_URL,
          hasToken: !!token,
          tokenPreview: token ? token.substring(0, 20) + '...' : 'sem token'
        });
      }
      
      this.socket = io(API_URL, {
        auth: {
          token: token
        },
        transports: ['websocket'], // APENAS WebSocket - sem fallback para polling
        timeout: 45000, // 45 segundos - mesmo valor do backend
        reconnection: true,
        reconnectionAttempts: 10, // Mais tentativas de reconexão
        reconnectionDelay: 1000, // 1 segundo de delay inicial
        reconnectionDelayMax: 5000, // 5 segundos de delay máximo
        autoConnect: true, // Conectar automaticamente
        withCredentials: true, // Enviar cookies e credenciais
        upgrade: false, // Desabilitar upgrade de polling para websocket
        forceNew: false // Reutilizar conexão existente
      });

      this.setupEventListeners();
      
    } catch (error) {
      console.error('❌ Erro ao conectar Socket.IO:', error);
    }
  }

  /**
   * 🔌 Configurar listeners de eventos
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Conexão estabelecida
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Entrar na sala de notificações
      this.socket.emit('join_notifications');
      
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Socket.IO conectado:', this.socket.id);
      }
    });

    // Desconexão
    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('❌ Socket.IO desconectado:', reason);
      }
      
      // Tentar reconectar se não foi intencional
      if (reason !== 'io client disconnect') {
        this.handleReconnect();
      }
    });

    // Erro de conexão
    this.socket.on('connect_error', (error) => {
      console.error('❌ Erro de conexão Socket.IO:', error);
      this.isConnected = false;
      this.handleReconnect();
    });

    // Nova notificação recebida
    this.socket.on('new_notification', (notification) => {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('📢 Nova notificação recebida:', notification);
      }
      this.handleNewNotification(notification);
    });

    // Confirmação de conexão
    this.socket.on('connected', (data) => {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 Confirmação de conexão:', data);
      }
    });
  }

  /**
   * 🔄 Gerenciar reconexão automática
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('❌ Máximo de tentativas de reconexão atingido');
      }
      return;
    }

    this.reconnectAttempts++;
    
    // Log apenas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 Tentativa de reconexão ${this.reconnectAttempts}/${this.maxReconnectAttempts} em ${this.reconnectInterval/1000}s...`);
    }

    setTimeout(() => {
      if (!this.isConnected) {
        this.socket?.connect();
      }
    }, this.reconnectInterval);
  }

  /**
   * 📢 Processar nova notificação
   */
  handleNewNotification(notification) {
    try {
      // Disparar evento customizado para o NotificationContext
      const event = new CustomEvent('socketNotification', {
        detail: notification
      });
      window.dispatchEvent(event);
      
      // Mostrar toast se disponível
      if (window.showNotificationToast) {
        window.showNotificationToast(notification);
      }
      
    } catch (error) {
      console.error('❌ Erro ao processar notificação:', error);
    }
  }

  /**
   * 🔌 Desconectar
   */
  disconnect() {
    if (this.socket) {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 Desconectando Socket.IO...');
      }
      this.socket.emit('leave_notifications');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * 📊 Obter status da conexão
   */
  getConnectionStatus() {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  /**
   * 🔍 Verificar se está conectado
   */
  isSocketConnected() {
    return this.isConnected && this.socket?.connected;
  }
}

// Singleton instance GLOBAL (sobrevive ao Hot Module Replacement)
const socketService = new SocketService();

// Debug: Verificar se há múltiplas instâncias (apenas em desenvolvimento)
if (module.hot && process.env.NODE_ENV === 'development') {
  console.log('🔥 HMR detectado - usando singleton global para evitar múltiplas conexões');
}

export default socketService;
